import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { ReactGrid, Column, Row, CellChange, TextCell, Id, MenuOption, SelectionMode } from "@silevis/reactgrid";
import "@silevis/reactgrid/styles.css";
import { 
    Box, 
    Button, 
    Grid, 
    Typography,
    Alert,
    CircularProgress,
    Snackbar
} from '@mui/material';
import { Download, Upload, Save, Add } from '@mui/icons-material';
import Loader from '../../../components/Loader';
import { exportBillableCombosCSVService, importBillableCombosCSVService } from '../../../services/billing';
import { useSnackBarState } from '../../../hooks';
import { Translate } from 'react-localize-redux';

// Constants
const ACTION_COLUMN_ID = 'action';
const ACTION_COLUMN_WIDTH = 80;

interface ComboRow {
    name: string;
    code: string;
    billableConcepts: string; // Comma-separated list
    action?: '' | 'DELETE'; // Action column for deletion
}

interface BillableComboConfigurationProps {
    uuidInvestigation: string;
    idBillingInfo: number;
    onCombosUpdated: () => void;
}

const BillableComboConfiguration: React.FC<BillableComboConfigurationProps> = ({
    uuidInvestigation,
    idBillingInfo,
    onCombosUpdated
}) => {
    // State
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [combos, setCombos] = useState<ComboRow[]>([]);
    const [showSnackbar, setShowSnackbar, handleCloseSnackbar] = useSnackBarState();
    const [hasChanges, setHasChanges] = useState(false);
    const [columnWidths, setColumnWidths] = useState<Record<string, number>>({
        [ACTION_COLUMN_ID]: ACTION_COLUMN_WIDTH,
        'name': 250,
        'code': 150,
        'billableConcepts': 400,
    });

    // Fetch combos on mount
    useEffect(() => {
        if (uuidInvestigation && idBillingInfo) {
            fetchCombos();
        }
    }, [uuidInvestigation, idBillingInfo]);

    // Helper function to extract error message
    const extractErrorMessage = (error: any, defaultMessage: string): string => {
        if (error?.response?.data?.errorMessage) {
            return error.response.data.errorMessage;
        } else if (error?.response?.data?.message) {
            return error.response.data.message;
        } else if (error?.response?.data?.error) {
            return error.response.data.error;
        } else if (error?.response?.data && typeof error.response.data === 'string') {
            return error.response.data;
        } else if (error?.message) {
            return error.message;
        } else if (typeof error === 'string') {
            return error;
        }
        return defaultMessage;
    };

    // Helper function to show error snackbar
    const showErrorSnackbar = useCallback((message: string) => {
        setShowSnackbar({ show: false });
        setTimeout(() => {
            setShowSnackbar({
                show: true,
                message: message,
                severity: "error"
            });
        }, 100);
    }, [setShowSnackbar]);

    // Helper to show success notification
    const showSuccessNotification = useCallback((message: string) => {
        setShowSnackbar({
            show: true,
            message: message,
            severity: "info"
        });
    }, [setShowSnackbar]);

    const fetchCombos = async () => {
        if (!uuidInvestigation || !idBillingInfo) return;
        
        try {
            setLoading(true);
            const response = await exportBillableCombosCSVService(uuidInvestigation, idBillingInfo);
            
            if (response.status === 200) {
                const parsed = parseCSV(response.csv);
                setCombos(parsed);
                setHasChanges(false);
            }
        } catch (error: any) {
            const errorMessage = extractErrorMessage(error, "Failed to load billable combos");
            showErrorSnackbar(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    // Parse CSV into structured data
    const parseCSV = (csvContent: string): ComboRow[] => {
        const lines = csvContent.trim().split('\n');
        if (lines.length <= 1) {
            return [];
        }
        
        // Skip header row
        const rows: ComboRow[] = lines.slice(1).map(line => {
            const values = parseCSVLine(line);
            return {
                action: values[0]?.toUpperCase() === 'DELETE' ? 'DELETE' : '',
                name: values[1] || '',
                code: values[2] || '',
                billableConcepts: values[3] || ''
            };
        });
        
        return rows;
    };

    // Handle CSV quoted fields properly
    const parseCSVLine = (line: string): string[] => {
        const result: string[] = [];
        let current = '';
        let inQuotes = false;
        
        for (let i = 0; i < line.length; i++) {
            const char = line[i];
            
            if (char === '"') {
                if (inQuotes && line[i + 1] === '"') {
                    current += '"';
                    i++;
                } else {
                    inQuotes = !inQuotes;
                }
            } else if (char === ',' && !inQuotes) {
                result.push(current);
                current = '';
            } else {
                current += char;
            }
        }
        result.push(current);
        
        return result.map(v => v.trim());
    };

    // Convert back to CSV
    const generateCSV = (): string => {
        const headers = ['Action', 'ComboName', 'ComboCode', 'BillableConcepts'];
        
        const rows = combos.map(row => {
            const escapeName = (name: string) => {
                if (name.includes(',') || name.includes('"') || name.includes('\n')) {
                    return `"${name.replace(/"/g, '""')}"`;
                }
                return name;
            };
            
            return [
                row.action || '',
                escapeName(row.name),
                row.code,
                escapeName(row.billableConcepts)
            ].join(',');
        });
        
        return [headers.join(','), ...rows].join('\n');
    };

    // Save changes
    const handleSave = async () => {
        if (!uuidInvestigation || !idBillingInfo) return;
        
        try {
            setSaving(true);
            const csvContent = generateCSV();
            const response = await importBillableCombosCSVService(
                uuidInvestigation,
                idBillingInfo,
                csvContent
            );
            
            if (response.status >= 200 && response.status < 300) {
                setShowSnackbar({
                    show: true,
                    message: "Billable combos updated successfully!",
                    severity: "success"
                });
                setHasChanges(false);
                onCombosUpdated();
                await fetchCombos();
            }
        } catch (error: any) {
            const errorMessage = extractErrorMessage(error, "Failed to save billable combos");
            showErrorSnackbar(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    // Export as CSV file
    const handleExport = () => {
        const csvContent = generateCSV();
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `billable_combos_${uuidInvestigation}_${Date.now()}.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
    };

    // Import from CSV file
    const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        
        const reader = new FileReader();
        reader.onload = (e) => {
            const content = e.target?.result as string;
            const parsed = parseCSV(content);
            setCombos(parsed);
            setHasChanges(true);
        };
        reader.readAsText(file);
        
        event.target.value = '';
    };

    // Add new row
    const handleAddRow = () => {
        const newRow: ComboRow = {
            name: '',
            code: '',
            billableConcepts: '',
            action: ''
        };
        setCombos([...combos, newRow]);
        setHasChanges(true);
        showSuccessNotification('New row added. Remember to save your changes.');
    };

    // ReactGrid columns configuration
    const columns = useMemo((): Column[] => [
        { columnId: ACTION_COLUMN_ID, width: columnWidths[ACTION_COLUMN_ID], resizable: false },
        { columnId: 'name', width: columnWidths['name'], resizable: true },
        { columnId: 'code', width: columnWidths['code'], resizable: true },
        { columnId: 'billableConcepts', width: columnWidths['billableConcepts'], resizable: true },
    ], [columnWidths]);

    // ReactGrid rows
    const rows = useMemo((): Row[] => {
        const headerRow: Row = {
            rowId: 'header',
            cells: [
                { type: 'header', text: 'Action' },
                { type: 'header', text: 'Combo Name' },
                { type: 'header', text: 'Combo Code' },
                { type: 'header', text: 'Billable Concepts' }
            ]
        };
        
        const dataRows: Row[] = combos.map((combo, index) => {
            const isDeleted = combo.action === 'DELETE';
            return {
                rowId: index,
                cells: [
                    { 
                        type: 'text', 
                        text: isDeleted ? '❌' : '🗑️',
                        nonEditable: false,
                        className: isDeleted ? 'deleted-row' : ''
                    },
                    { 
                        type: 'text', 
                        text: combo.name,
                        className: isDeleted ? 'deleted-row' : ''
                    },
                    { 
                        type: 'text', 
                        text: combo.code,
                        className: isDeleted ? 'deleted-row' : ''
                    },
                    { 
                        type: 'text', 
                        text: combo.billableConcepts,
                        className: isDeleted ? 'deleted-row' : ''
                    }
                ]
            };
        });
        
        // Add footer row with "+" button to add new rows
        const footerRow: Row = {
            rowId: 'add-row-footer',
            cells: [
                { 
                    type: 'text', 
                    text: '➕',
                    nonEditable: false,
                    className: 'add-row-cell'
                },
                { 
                    type: 'text', 
                    text: 'Click + to add a new row',
                    nonEditable: true,
                    className: 'add-row-hint'
                },
                { 
                    type: 'text', 
                    text: '',
                    nonEditable: true
                },
                { 
                    type: 'text', 
                    text: '',
                    nonEditable: true
                }
            ]
        };
        
        return [headerRow, ...dataRows, footerRow];
    }, [combos]);

    // Handle changes - toggle action on click, update other cells
    const handleChanges = useCallback((changes: CellChange[]) => {
        const updatedCombos = [...combos];
        let hasActionToggle = false;
        
        changes.forEach(change => {
            const rowIndex = change.rowId as number;
            const columnId = change.columnId as string;
            
            if (rowIndex >= 0) {
                // Handle action column toggle
                if (columnId === ACTION_COLUMN_ID) {
                    const currentAction = updatedCombos[rowIndex].action;
                    updatedCombos[rowIndex].action = currentAction === 'DELETE' ? '' : 'DELETE';
                    hasActionToggle = true;
                }
                // Handle regular cell edits
                else {
                    const newCell = change.newCell as TextCell;
                    const newValue = newCell.text;
                    
                    if (columnId === 'name') {
                        updatedCombos[rowIndex].name = newValue;
                    } else if (columnId === 'code') {
                        updatedCombos[rowIndex].code = newValue;
                    } else if (columnId === 'billableConcepts') {
                        updatedCombos[rowIndex].billableConcepts = newValue;
                    }
                }
                
                setHasChanges(true);
            }
        });
        
        setCombos(updatedCombos);
    }, [combos]);

    // Handle focus changes - trigger action toggle on click or add row
    const handleFocusLocationChange = useCallback((location: { rowId: Id; columnId: Id }) => {
        // Handle add row footer click
        if (location.rowId === 'add-row-footer' && location.columnId === ACTION_COLUMN_ID) {
            const newRow: ComboRow = {
                name: '',
                code: '',
                billableConcepts: '',
                action: ''
            };
            setCombos([...combos, newRow]);
            setHasChanges(true);
            showSuccessNotification('New row added. Remember to save your changes.');
            return;
        }
        
        if (location.columnId === ACTION_COLUMN_ID && typeof location.rowId === 'number') {
            const updatedCombos = [...combos];
            const rowIndex = location.rowId;
            const row = updatedCombos[rowIndex];
            const currentAction = row.action;
            
            // Check if row is empty (all data fields are empty)
            const isEmptyRow = !row.name?.trim() && !row.code?.trim() && !row.billableConcepts?.trim();
            
            if (isEmptyRow) {
                // Remove empty row entirely
                updatedCombos.splice(rowIndex, 1);
                showSuccessNotification('Empty row removed.');
            } else {
                // Toggle DELETE action for non-empty rows
                updatedCombos[rowIndex].action = currentAction === 'DELETE' ? '' : 'DELETE';
            }
            
            setCombos(updatedCombos);
            setHasChanges(true);
        }
    }, [combos, showSuccessNotification]);

    // Handle column resize
    const handleColumnResize = useCallback((columnId: Id, width: number, selectedColIds: Id[]) => {
        setColumnWidths(prev => ({
            ...prev,
            [columnId as string]: width
        }));
    }, []);

    if (loading) {
        return <Loader />;
    }

    if (!idBillingInfo) {
        return (
            <Alert severity="error">
                No billing info available
            </Alert>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            {/* Action Bar */}
            <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Grid item>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
                        onClick={handleSave}
                        disabled={!hasChanges || loading || saving}
                    >
                        {saving ? 'Saving...' : 'Save'}
                    </Button>
                </Grid>
                <Grid item>
                    <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<Add />}
                        onClick={handleAddRow}
                        disabled={loading || saving}
                    >
                        Add Row
                    </Button>
                </Grid>
                <Grid item>
                    <Button
                        variant="outlined"
                        startIcon={<Download />}
                        onClick={handleExport}
                        disabled={loading || saving || combos.length === 0}
                    >
                        Export CSV
                    </Button>
                </Grid>
                <Grid item>
                    <Button
                        variant="outlined"
                        component="label"
                        startIcon={<Upload />}
                        disabled={loading || saving}
                    >
                        Import CSV
                        <input
                            type="file"
                            hidden
                            accept=".csv"
                            onChange={handleImport}
                        />
                    </Button>
                </Grid>
            </Grid>

            {/* Excel-like Table or Empty State */}
            {combos.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        No billable combos found
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                        To get started, import a CSV file with your billable combos configuration.
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        CSV Format: ComboName,ComboCode,BillableConcepts (comma-separated)
                    </Typography>
                </Alert>
            ) : (
                <Box sx={{ 
                    width: '100%', 
                    height: '600px', 
                    overflow: 'auto', 
                    border: '1px solid #ddd', 
                    backgroundColor: '#fff',
                    '& .rg-cell:first-of-type': {
                        cursor: 'pointer',
                        transition: 'background-color 0.2s',
                        textAlign: 'center',
                        fontSize: '18px',
                        '&:hover': {
                            backgroundColor: '#f0f0f0',
                        }
                    },
                    '& .deleted-row': {
                        backgroundColor: '#ffcccc !important',
                        textDecoration: 'line-through',
                        opacity: 0.8,
                        color: '#666'
                    },
                    '& .add-row-cell': {
                        backgroundColor: '#e3f2fd !important',
                        fontSize: '20px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        '&:hover': {
                            backgroundColor: '#bbdefb !important',
                        }
                    },
                    '& .add-row-hint': {
                        backgroundColor: '#e3f2fd !important',
                        fontStyle: 'italic',
                        color: '#1976d2'
                    }
                }}>
                    <ReactGrid
                        rows={rows}
                        columns={columns}
                        onCellsChanged={handleChanges}
                        onFocusLocationChanged={handleFocusLocationChange}
                        onColumnResized={handleColumnResize}
                        enableRangeSelection
                        enableFillHandle
                        enableColumnSelection
                        enableRowSelection
                        stickyTopRows={1}
                    />
                </Box>
            )}

            {/* Info Message */}
            <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                    <strong>How to use:</strong>
                </Typography>
                <ul style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                    <li>Click the trash icon (🗑️) to mark a row for deletion - it will turn red with an ❌</li>
                    <li>Click the ❌ icon to undo and restore the row</li>
                    <li>Click any cell to edit its value</li>
                    <li>BillableConcepts should be comma-separated names (e.g., "VPA,Bloc opératoire,Forfait bilan")</li>
                    <li>Remember to save your changes when done</li>
                </ul>
            </Alert>

            {/* Snackbar for feedback messages */}
            <Snackbar
                open={showSnackbar.show}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert 
                    onClose={handleCloseSnackbar} 
                    severity={showSnackbar.severity || 'info'}
                    sx={{ width: '100%', minWidth: '400px' }}
                >
                    {showSnackbar.message && (
                        typeof showSnackbar.message === 'string' && showSnackbar.message.startsWith('hospital.') 
                            ? <Translate id={showSnackbar.message} />
                            : showSnackbar.message
                    )}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default BillableComboConfiguration;
